import React, { useEffect, useRef, useState } from 'react';
import {
  CHALLENGE_COURSE_SPEND_OPTIONS,
  CHALLENGE_INVESTING_EXPERIENCE_OPTIONS,
  type ChallengeCourseSpend,
  type ChallengeInvestingExperience,
} from './challengeQualifyingLead';
import './ChallengeFormQuestions.css';

export type ChallengeFormQuestionAnswers = {
  investingExperience?: ChallengeInvestingExperience;
  courseSpend?: ChallengeCourseSpend;
};
export type ChallengeFormQuestionKey = keyof ChallengeFormQuestionAnswers;

const QUESTIONS = {
  investingExperience: {
    step: 2,
    text: 'How long have you been investing or trading?',
    options: CHALLENGE_INVESTING_EXPERIENCE_OPTIONS,
  },
  courseSpend: {
    step: 3,
    text: 'How much have you spent on trading or investing courses?',
    options: CHALLENGE_COURSE_SPEND_OPTIONS,
  },
} as const;

/**
 * How long a question's answers ignore pointer taps after it appears. The
 * answers land where the visitor just pressed Save (or the previous answer),
 * so the second half of a quick double tap would otherwise answer a question
 * they have not read, and send Google Ads a wrong value (Rob, 2026-10-02).
 * Keyboard presses are never held back.
 */
export const CHALLENGE_QUESTION_TAP_GUARD_MS = 350;

/**
 * Steps 2 and 3 of the free challenge signup, inside the same form box as
 * step 1 (Rob, 2026-10-02). The seat is already saved. One question at a time;
 * a tap saves the answer and moves on, so there is no Next or Submit button.
 * The page reports every tap and finishes once both are in.
 *
 * It never scrolls the page. Focus moves to the question for screen readers
 * with preventScroll, so the box stays exactly where the visitor is looking.
 */
export default function ChallengeFormQuestions({
  current,
  onShow,
  onAnswer,
  notice = null,
}: {
  /** The question on show. The page owns it so the form box can name its step. */
  current: ChallengeFormQuestionKey;
  onShow: (question: ChallengeFormQuestionKey) => void;
  onAnswer: (answers: ChallengeFormQuestionAnswers, changed: ChallengeFormQuestionKey) => void;
  notice?: React.ReactNode;
}): React.ReactElement {
  const [answers, setAnswers] = useState<ChallengeFormQuestionAnswers>({});
  const finished = useRef(false);
  const questionRef = useRef<HTMLParagraphElement>(null);
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  const pointerStartedWhileGuarded = useRef(false);

  useEffect(() => {
    questionRef.current?.focus({ preventScroll: true });
    readyRef.current = false;
    setReady(false);
    const timer = window.setTimeout(() => {
      readyRef.current = true;
      setReady(true);
    }, CHALLENGE_QUESTION_TAP_GUARD_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  const choose = (value: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (finished.current) return;
    // detail is 0 for a keyboard press, 1+ for a pointer tap.
    if (event.detail > 0) {
      const guardedPress = pointerStartedWhileGuarded.current;
      pointerStartedWhileGuarded.current = false;
      // A press that began before the answers were ready stays ignored even
      // when its release arrives after the guard expires.
      if (guardedPress || !readyRef.current) return;
    }
    const next: ChallengeFormQuestionAnswers = { ...answers, [current]: value };
    setAnswers(next);
    if (current === 'investingExperience') onShow('courseSpend');
    else finished.current = true;
    onAnswer(next, current);
  };

  const question = QUESTIONS[current];
  const selected = answers[current] ?? null;

  return (
    <div
      className="chfq"
      data-testid="challenge-qualifying-step"
      data-question={current}
      data-ready={ready ? 'true' : 'false'}
      role="group"
      aria-labelledby="chfq-question"
    >
      {notice ? (
        <p className="chfq-notice" role="status">
          {notice}
        </p>
      ) : null}
      <p className="chfq-progress">Step {question.step} of 3</p>
      <p className="chfq-question" id="chfq-question" ref={questionRef} tabIndex={-1}>
        {question.text}
      </p>
      <div className="chfq-options" data-testid={`challenge-qualifying-${current}`}>
        {question.options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="chfq-option"
            aria-pressed={selected === option.value}
            data-value={option.value}
            onPointerDown={() => {
              pointerStartedWhileGuarded.current = !readyRef.current;
            }}
            onPointerCancel={() => {
              pointerStartedWhileGuarded.current = false;
            }}
            onClick={(event) => choose(option.value, event)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {current === 'courseSpend' ? (
        <button type="button" className="chfq-back" onClick={() => onShow('investingExperience')}>
          Back
        </button>
      ) : null}
    </div>
  );
}
